import { Ref, useEffect, useState } from 'react';

import AssignmentsList from 'src/components/assignments/assignments-list';
import { NoAssignmentDataView } from 'src/components/assignments/no-assignment-data-view';
import { LoadingScreen } from 'src/components/common/loaders';
import { CommonXApiPayload } from 'src/hooks/use-common-xapi-context-payload.hook';
import useEffectOnce from 'src/hooks/use-effect-once.hook';
import { useInfiniteScroll } from 'src/hooks/use-infinite-scroll.hook';
import { usePrevious } from 'src/hooks/use-previous.hook';
import {
  Assignment,
  AssignmentsViewOptions,
} from 'src/interfaces/assignment.types';
import { XApiEventKeys } from 'src/interfaces/event.types';
import authService from 'src/services/api/auth.service';
import eventService from 'src/services/event.service';
import {
  useActiveAssignmentsViewSelector,
  useIsNoAssignmentsDataPastWorkSelector,
  useIsNoAssignmentsDataToDoSelector,
} from 'src/store/features';

import { ElementarySchoolAssignmentsWrapper } from '../elementary-school-assignments-wrapper';

export const ElementarySchoolSection = ({
  sectionRef,
  isAssignmentDataLoading,
  isAssignmentDataFetching,
  itemsToDisplay,
  incrementItemsToDisplayCount,
  commonXapiContextPayload,
  section,
}: {
  sectionRef: Ref<HTMLDivElement>;
  isAssignmentDataLoading: boolean;
  isAssignmentDataFetching: boolean;
  itemsToDisplay: Assignment[] | undefined;
  incrementItemsToDisplayCount: () => void;
  commonXapiContextPayload: CommonXApiPayload;
  section: AssignmentsViewOptions.PAST_WORK | AssignmentsViewOptions.TODO;
}) => {
  const isStudent = authService.isStudent();

  const isNoAssignmentsDataToDo = useIsNoAssignmentsDataToDoSelector();
  const isNoAssignmentsDataPastWork = useIsNoAssignmentsDataPastWorkSelector();

  const isNoAssignmentsDataView =
    section === AssignmentsViewOptions.TODO
      ? isNoAssignmentsDataToDo
      : isNoAssignmentsDataPastWork;

  const { isLoadMoreRefVisible, setLoadMoreRef } = useInfiniteScroll();

  const activeAssignmentsView = useActiveAssignmentsViewSelector();

  const prevActiveAssignmentsView = usePrevious(activeAssignmentsView);

  const [
    isAssignmentsFetchingOnSectionChange,
    setIsAssignmentsFetchingOnSectionChange,
  ] = useState(false);

  // show empty stream until new assignments received to prevent display of previous one
  useEffect(() => {
    if (!prevActiveAssignmentsView) return;

    const isFetchingOnSectionChange =
      activeAssignmentsView !== prevActiveAssignmentsView &&
      isAssignmentDataFetching;

    setIsAssignmentsFetchingOnSectionChange(isFetchingOnSectionChange);
    // Avoid adding prevActiveAssignmentsView to the deps as we only track the active one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAssignmentDataFetching, activeAssignmentsView]);

  useEffect(() => {
    if (isLoadMoreRefVisible) {
      incrementItemsToDisplayCount();
    }
  }, [isLoadMoreRefVisible, incrementItemsToDisplayCount]);

  useEffectOnce(() => {
    eventService.send(XApiEventKeys.viewed, undefined, {
      result: {
        isRightClick: false,
        ...commonXapiContextPayload,
      },
    });
  }, [itemsToDisplay]);

  if (isAssignmentDataLoading) {
    return (
      <ElementarySchoolAssignmentsWrapper wrapperRef={sectionRef}>
        {isStudent ? <LoadingScreen isTransparentVersion={false} /> : null}
      </ElementarySchoolAssignmentsWrapper>
    );
  }

  if (!itemsToDisplay || isAssignmentsFetchingOnSectionChange) {
    return (
      <ElementarySchoolAssignmentsWrapper wrapperRef={sectionRef}>
        {null}
      </ElementarySchoolAssignmentsWrapper>
    );
  }

  if (isNoAssignmentsDataView || !itemsToDisplay.length) {
    return (
      <ElementarySchoolAssignmentsWrapper wrapperRef={sectionRef}>
        <NoAssignmentDataView isMiddleSchoolView={false} k5Section={section} />
      </ElementarySchoolAssignmentsWrapper>
    );
  }

  return (
    <ElementarySchoolAssignmentsWrapper wrapperRef={sectionRef}>
      <AssignmentsList assignments={itemsToDisplay} k5Section={section} />
      <div ref={setLoadMoreRef} />
    </ElementarySchoolAssignmentsWrapper>
  );
};
